
/**
 * Cart Expiry Management for Theme
 * 
 * This script manages cart expiry functionality, automatically clearing
 * carts after 30 minutes of inactivity and integrating with the webhook system.
 */

(function() {
    'use strict';
    
    // ============================================================================
    // CONFIGURATION
    // ============================================================================
    
    const CART_EXPIRY_MINUTES = 30;
    const CART_EXPIRY_KEY = 'cart_expiry_timestamp';
    const CART_DATA_KEY = 'cart_data_backup';
    const CART_EXPIRY_CHECK_INTERVAL = 60000; // Check every minute
    const CART_EXPIRY_API_URL = 'https://shopify.infinitediscs.com/api/cart_expiry.php';
    const DEBUG_MODE = true; // Set to false in production
    
    // ============================================================================
    // UTILITY FUNCTIONS
    // ============================================================================
    
    /**
     * Check if cart has expired
     */
    function isCartExpired() {
        const expiryTime = localStorage.getItem(CART_EXPIRY_KEY);
        if (!expiryTime) {
            if (DEBUG_MODE) {
                console.log('[Cart Expiry] No expiry timestamp found, cart not expired');
            }
            return false;
        }
        
        const now = Date.now();
        const lastActivity = parseInt(expiryTime);
        const expiryDuration = CART_EXPIRY_MINUTES * 60 * 1000; // Convert to milliseconds
        const timeSinceLastActivity = now - lastActivity;
        const isExpired = timeSinceLastActivity >= expiryDuration;
        
        if (DEBUG_MODE) {
            console.log('[Cart Expiry] Checking expiry status:', {
                lastActivity: new Date(lastActivity).toISOString(),
                currentTime: new Date(now).toISOString(),
                timeSinceLastActivity: Math.round(timeSinceLastActivity / 60000) + ' minutes',
                expiryDuration: CART_EXPIRY_MINUTES + ' minutes',
                isExpired: isExpired
            });
        }
        
        return isExpired;
    }
    
    /**
     * Set cart expiry timestamp (resets timer on activity)
     */
    function setCartExpiry() {
        const now = Date.now();
        const expiryTime = new Date(now + (CART_EXPIRY_MINUTES * 60 * 1000));
        localStorage.setItem(CART_EXPIRY_KEY, now.toString());
        
        if (DEBUG_MODE) {
            console.log('[Cart Expiry] Cart expiry timer reset:', {
                currentTime: new Date(now).toISOString(),
                expiryTime: expiryTime.toISOString(),
                expiresInMinutes: CART_EXPIRY_MINUTES,
                timestamp: now
            });
        } else {
            console.log('[Cart Expiry] Cart expiry timer reset - expires in ' + CART_EXPIRY_MINUTES + ' minutes');
        }
    }
    
    /**
     * Clear cart expiry
     */
    function clearCartExpiry() {
        localStorage.removeItem(CART_EXPIRY_KEY);
        localStorage.removeItem(CART_DATA_KEY);
        console.log('[Cart Expiry] Cart expiry cleared');
    }
    
    /**
     * Get remaining time until cart expires
     */
    function getRemainingTime() {
        const expiryTime = localStorage.getItem(CART_EXPIRY_KEY);
        if (!expiryTime) return null;
        
        const now = Date.now();
        const lastActivity = parseInt(expiryTime);
        const expiryDuration = CART_EXPIRY_MINUTES * 60 * 1000;
        const elapsed = now - lastActivity;
        const remaining = expiryDuration - elapsed;
        
        return remaining > 0 ? remaining : 0;
    }
    
    /**
     * Format time in MM:SS format
     */
    function formatTime(milliseconds) {
        const totalSeconds = Math.floor(milliseconds / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    
    // ============================================================================
    // CART CLEARING FUNCTIONS
    // ============================================================================
    
    /**
     * Clear cart via Shopify API
     */
    function clearCartViaAPI() {
        return fetch('/cart/clear.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            }
        })
        .then(response => response.json())
        .then(data => {
            console.log('[Cart Expiry] Cart cleared via API:', data);
            
            // Send cart expiry webhook
            sendCartExpiryWebhook(data);
            
            return data;
        })
        .catch(error => {
            console.error('[Cart Expiry] Error clearing cart via API:', error);
            throw error;
        });
    }
    
    /**
     * Send cart expiry webhook
     */
    function sendCartExpiryWebhook(cartData) {
        try {
            // Try multiple sources for API key and shop configuration
            const config = window.DAWN_WEBHOOK_CONFIG || window.CART_WEBHOOK_CONFIG || {};
            const apiKey = config.apiKey || '';
            const shop = config.shop || window.location.hostname;
            
            if (!apiKey || !shop) {
                console.warn('[Cart Expiry] Missing API key or shop for webhook', {
                    hasDawnConfig: !!window.DAWN_WEBHOOK_CONFIG,
                    hasCartConfig: !!window.CART_WEBHOOK_CONFIG,
                    apiKey: apiKey ? 'present' : 'missing',
                    shop: shop
                });
                return;
            }
            
            // Extract variant IDs from cart items for better tracking
            const variantIds = [];
            if (cartData.items && cartData.items.length > 0) {
                cartData.items.forEach(item => {
                    if (item.variant_id) {
                        variantIds.push(item.variant_id);
                    }
                });
            }
            
            const webhookData = {
                event_type: 'carts/expired',
                shop: shop,
                timestamp: new Date().toISOString(),
                source: 'cart_expiry_system',
                api_key: apiKey, // Include API key in payload
                data: {
                    action: 'cart_expired',
                    cart_token: cartData.token || null,
                    item_count: cartData.item_count || 0,
                    total_price: cartData.total_price || 0,
                    items: cartData.items || [],
                    variant_ids: variantIds, // Include variant IDs for tracking
                    customer: cartData.customer || null,
                    attributes: cartData.attributes || {},
                    expiry_reason: 'timeout',
                    expired_at: new Date().toISOString()
                }
            };
            
            // Send to theme webhook endpoint
            fetch('https://shopify.lineztechnologies.com/webhook/theme_webhook.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-API-Key': apiKey,
                    'X-Shopify-Shop-Domain': shop,
                    'X-Shopify-Topic': 'carts/expired'
                },
                body: JSON.stringify(webhookData)
            })
            .then(response => response.json())
            .then(result => {
                console.log('[Cart Expiry] Webhook sent successfully:', result);
            })
            .catch(error => {
                console.error('[Cart Expiry] Error sending webhook:', error);
            });
            
        } catch (error) {
            console.error('[Cart Expiry] Error preparing webhook:', error);
        }
    }
    
    /**
     * Clear cart via server-side API
     */
    function clearCartViaServer() {
        const config = window.DAWN_WEBHOOK_CONFIG || {};
        const apiKey = config.apiKey || '';
        const shop = config.shop || '';
        
        if (!apiKey || !shop) {
            console.warn('[Cart Expiry] Missing API key or shop for server-side cart clearing');
            return Promise.resolve();
        }
        
        const url = `${CART_EXPIRY_API_URL}?shop=${encodeURIComponent(shop)}`;
        
        return fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-API-Key': apiKey
            },
            body: JSON.stringify({
                shop: shop,
                action: 'clear_expired'
            })
        })
        .then(response => response.json())
        .then(data => {
            console.log('[Cart Expiry] Server-side cart clearing result:', data);
            return data;
        })
        .catch(error => {
            console.error('[Cart Expiry] Error with server-side cart clearing:', error);
            throw error;
        });
    }
    
    // ============================================================================
    // NOTIFICATION FUNCTIONS
    // ============================================================================
    
    /**
     * Show cart expiry notification
     */
    function showCartExpiryNotification() {
        // Remove existing notifications
        const existingNotifications = document.querySelectorAll('.cart-expiry-notification');
        existingNotifications.forEach(notification => notification.remove());
        
        const notification = document.createElement('div');
        notification.className = 'cart-expiry-notification';
        notification.innerHTML = `
            <div style="
                position: fixed; 
                top: 20px; 
                right: 20px; 
                background: #f44336; 
                color: white; 
                padding: 15px 20px; 
                border-radius: 8px; 
                z-index: 9999;
                box-shadow: 0 4px 12px rgba(0,0,0,0.3);
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                font-size: 14px;
                max-width: 300px;
                animation: slideIn 0.3s ease-out;
            ">
                <div style="font-weight: 600; margin-bottom: 5px;">🕒 Cart Expired</div>
                <div>Your cart has expired due to inactivity. Please add items again.</div>
                <button onclick="this.parentElement.parentElement.remove()" style="
                    background: rgba(255,255,255,0.2); 
                    border: none; 
                    color: white; 
                    padding: 5px 10px; 
                    border-radius: 4px; 
                    cursor: pointer; 
                    margin-top: 10px;
                    font-size: 12px;
                ">Dismiss</button>
            </div>
            <style>
                @keyframes slideIn {
                    from { transform: translateX(100%); opacity: 0; }
                    to { transform: translateX(0); opacity: 1; }
                }
            </style>
        `;
        
        document.body.appendChild(notification);
        
        // Auto-remove after 10 seconds
        setTimeout(() => {
            if (notification.parentElement) {
                notification.remove();
            }
        }, 10000);
    }
    
    /**
     * Show cart expiry warning (5 minutes before expiry)
     */
    function showCartExpiryWarning() {
        // Remove existing warnings
        const existingWarnings = document.querySelectorAll('.cart-expiry-warning');
        existingWarnings.forEach(warning => warning.remove());
        
        const warning = document.createElement('div');
        warning.className = 'cart-expiry-warning';
        warning.innerHTML = `
            <div style="
                position: fixed; 
                top: 20px; 
                right: 20px; 
                background: #ff9800; 
                color: white; 
                padding: 15px 20px; 
                border-radius: 8px; 
                z-index: 9998;
                box-shadow: 0 4px 12px rgba(0,0,0,0.3);
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                font-size: 14px;
                max-width: 300px;
                animation: slideIn 0.3s ease-out;
            ">
                <div style="font-weight: 600; margin-bottom: 5px;">⚠️ Cart Expiring Soon</div>
                <div>Your cart will expire in 5 minutes due to inactivity.</div>
                <button onclick="this.parentElement.parentElement.remove()" style="
                    background: rgba(255,255,255,0.2); 
                    border: none; 
                    color: white; 
                    padding: 5px 10px; 
                    border-radius: 4px; 
                    cursor: pointer; 
                    margin-top: 10px;
                    font-size: 12px;
                ">Dismiss</button>
            </div>
        `;
        
        document.body.appendChild(warning);
        
        // Auto-remove after 30 seconds
        setTimeout(() => {
            if (warning.parentElement) {
                warning.remove();
            }
        }, 30000);
    }
    
    // ============================================================================
    // MAIN CART EXPIRY LOGIC
    // ============================================================================
    
    /**
     * Auto-clear expired cart
     */
    function clearExpiredCart() {
        const expiryTime = localStorage.getItem(CART_EXPIRY_KEY);
        const remaining = getRemainingTime();
        
        console.log('[Cart Expiry] Checking cart expiry...', {
            hasExpiryTime: !!expiryTime,
            remainingTime: remaining ? Math.round(remaining / 1000) + 's' : 'N/A',
            isExpired: isCartExpired()
        });
        
        if (isCartExpired()) {
            console.log('[Cart Expiry] Cart expired, clearing...');
            
            // Try to clear cart via API first
            clearCartViaAPI()
                .then((cartData) => {
                    console.log('[Cart Expiry] Cart cleared via API:', cartData);
                    // Also clear server-side cart holds
                    return clearCartViaServer();
                })
                .then((serverData) => {
                    console.log('[Cart Expiry] Server-side cart holds cleared:', serverData);
                    clearCartExpiry();
                    
                    // Trigger cart update event
                    document.dispatchEvent(new CustomEvent('cart:updated'));
                    
                    // Show notification to user
                    showCartExpiryNotification();
                    
                    console.log('[Cart Expiry] Cart cleared successfully');
                })
                .catch(error => {
                    console.error('[Cart Expiry] Error clearing expired cart:', error);
                    
                    // Still show notification even if clearing failed
                    showCartExpiryNotification();
                });
        } else {
            console.log('[Cart Expiry] Cart is not expired according to local timer, skipping clear');
        }
    }
    
    /**
     * Force clear cart when server indicates expiry (bypasses local timer check)
     */
    function forceClearCart() {
        console.log('[Cart Expiry] Force clearing cart due to server expiry indication');
        
        // Try to clear cart via API first
        clearCartViaAPI()
            .then((cartData) => {
                console.log('[Cart Expiry] Cart cleared via API:', cartData);
                // Also clear server-side cart holds
                return clearCartViaServer();
            })
            .then((serverData) => {
                console.log('[Cart Expiry] Server-side cart holds cleared:', serverData);
                clearCartExpiry();
                
                // Trigger cart update event
                document.dispatchEvent(new CustomEvent('cart:updated'));
                
                // Show notification to user
                showCartExpiryNotification();
                
                console.log('[Cart Expiry] Cart force-cleared successfully');
            })
            .catch(error => {
                console.error('[Cart Expiry] Error force-clearing cart:', error);
                
                // Still show notification even if clearing failed
                showCartExpiryNotification();
            });
    }
    
    /**
     * Check for expiry warning (5 minutes before expiry)
     */
    function checkExpiryWarning() {
        const remaining = getRemainingTime();
        if (remaining && remaining <= 300000) { // 5 minutes = 300,000 ms
            const existingWarnings = document.querySelectorAll('.cart-expiry-warning');
            if (existingWarnings.length === 0) {
                showCartExpiryWarning();
            }
        }
    }
    
    // ============================================================================
    // EVENT HANDLERS
    // ============================================================================
    
    /**
     * Handle cart updates
     */
    function handleCartUpdate() {
        // Reset expiry timer when cart is updated
        setCartExpiry();
        console.log('[Cart Expiry] Cart expiry timer reset');
    }
    
    /**
     * Handle cart clearing
     */
    function handleCartClear() {
        // Clear expiry when cart is cleared manually
        clearCartExpiry();
        console.log('[Cart Expiry] Cart cleared manually, expiry cleared');
    }
    
    /**
     * Handle user activity
     */
    function handleUserActivity() {
        // Check for expired cart when user becomes active
        clearExpiredCart();
    }
    
    /**
     * Handle page visibility change
     */
    function handleVisibilityChange() {
        if (!document.hidden) {
            // Check for expired cart when user returns to tab
            clearExpiredCart();
        }
    }
    
    /**
     * Check current cart state and set expiry if needed
     */
    function checkCartState() {
        // Try to get current cart from Shopify
        fetch('/cart.js')
            .then(response => response.json())
            .then(cart => {
                if (cart && cart.item_count > 0) {
                    console.log('[Cart Expiry] Cart has ' + cart.item_count + ' items');
                    
                    // Check if cart has been inactive for a while before checking server
                    const lastActivity = localStorage.getItem(CART_EXPIRY_KEY);
                    const now = Date.now();
                    const timeSinceLastActivity = lastActivity ? (now - parseInt(lastActivity)) : 0;
                    const inactiveThreshold = 5 * 60 * 1000; // 5 minutes
                    
                    if (timeSinceLastActivity > inactiveThreshold) {
                        console.log('[Cart Expiry] Cart has been inactive for ' + Math.round(timeSinceLastActivity / 60000) + ' minutes, checking server expiry status');
                        
                        // Check server-side expiry status only if cart has been inactive
                        checkServerExpiryStatus(cart)
                            .then(serverExpired => {
                                console.log('[Cart Expiry] Server expiry check result:', serverExpired);
                                if (serverExpired) {
                                    console.log('[Cart Expiry] Server indicates cart is expired, clearing immediately');
                                    forceClearCart();
                                } else {
                                    console.log('[Cart Expiry] Server indicates cart is active, setting expiry timer');
                                    setCartExpiry();
                                }
                            })
                            .catch(error => {
                                console.log('[Cart Expiry] Could not check server expiry status:', error);
                                // Fallback to setting expiry timer
                                setCartExpiry();
                            });
                    } else {
                        console.log('[Cart Expiry] Cart is recent, setting expiry timer without server check');
                        setCartExpiry();
                    }
                } else {
                    console.log('[Cart Expiry] Cart is empty, clearing expiry timer');
                    clearCartExpiry();
                }
            })
            .catch(error => {
                console.log('[Cart Expiry] Could not fetch cart state:', error);
            });
    }
    
    /**
     * Check server-side expiry status
     */
    function checkServerExpiryStatus(cart) {
        // Extract variant IDs from cart items
        const variantIds = cart.items.map(item => item.variant_id);
        
        // Get API key from Dawn webhook config or default
        const apiKey = (window.DAWN_WEBHOOK_CONFIG && window.DAWN_WEBHOOK_CONFIG.apiKey) || '';
        
        console.log('[Cart Expiry] Checking server expiry with:', {
            hasConfig: !!window.DAWN_WEBHOOK_CONFIG,
            hasApiKey: !!apiKey,
            apiKeyLength: apiKey ? apiKey.length : 0,
            variantIds: variantIds,
            shopDomain: window.location.hostname
        });
        
        if (!apiKey) {
            console.log('[Cart Expiry] No API key available, skipping server expiry check');
            return Promise.resolve(false);
        }
        
        // Call our API to check expiry status
        const url = `${CART_EXPIRY_API_URL}?shop=${encodeURIComponent(window.location.hostname)}`;
        
        console.log('[Cart Expiry] Calling API URL:', url);
        
        return fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-API-Key': apiKey
            },
            body: JSON.stringify({
                action: 'check_expiry',
                variant_ids: variantIds,
                shop_domain: window.location.hostname
            })
        })
        .then(response => response.json())
        .then(data => {
            console.log('[Cart Expiry] Server expiry check response:', data);
            return data.expired || false;
        })
        .catch(error => {
            console.log('[Cart Expiry] Error checking server expiry status:', error);
            return false;
        });
    }
    
    // ============================================================================
    // INITIALIZATION
    // ============================================================================
    
    /**
     * Initialize cart expiry system
     */
    function initCartExpiry() {
        console.log('[Cart Expiry] Initializing cart expiry system...');
        
        // Check for expired cart on page load
        clearExpiredCart();
        
        // Check current cart state and set initial expiry if cart has items
        checkCartState();
        
        // Set up event listeners for theme cart events
        document.addEventListener('cart:updated', handleCartUpdate);
        document.addEventListener('cart:cleared', handleCartClear);
        document.addEventListener('cart:item-added', handleCartUpdate);
        document.addEventListener('cart:item-removed', handleCartUpdate);
        document.addEventListener('cart:item-updated', handleCartUpdate);
        
        // Listen for theme specific events
        document.addEventListener('cart:refresh', handleCartUpdate);
        document.addEventListener('product-form:cart-update', handleCartUpdate);
        
        // Handle user activity (reset timer on interaction)
        document.addEventListener('click', handleUserActivity);
        document.addEventListener('keypress', handleUserActivity);
        document.addEventListener('scroll', handleUserActivity);
        
        // Handle page visibility changes
        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        // Check expiry every minute
        setInterval(() => {
            clearExpiredCart();
            checkExpiryWarning();
        }, CART_EXPIRY_CHECK_INTERVAL);
        
        // Check for expiry warning every 30 seconds
        setInterval(checkExpiryWarning, 30000);
        
        console.log('[Cart Expiry] Cart expiry system initialized successfully');
    }
    
    // ============================================================================
    // PUBLIC API
    // ============================================================================
    
    // Export functions for external use
    window.CartExpiry = {
        isExpired: isCartExpired,
        clearExpired: clearExpiredCart,
        setExpiry: setCartExpiry,
        clearExpiry: clearCartExpiry,
        getRemainingTime: getRemainingTime,
        formatTime: formatTime,
        showNotification: showCartExpiryNotification,
        showWarning: showCartExpiryWarning,
        config: {
            expiryMinutes: CART_EXPIRY_MINUTES,
            checkInterval: CART_EXPIRY_CHECK_INTERVAL
        }
    };
    
    // ============================================================================
    // START THE SYSTEM
    // ============================================================================
    
    // Start the system when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initCartExpiry);
    } else {
        initCartExpiry();
    }
    
})();

